import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { Placement } from "react-bootstrap/types";
import { FaEdit, FaClock } from "react-icons/fa";
import {
  PauseCircleFill,
  Search,
  SendFill,
  FileEarmarkXFill,
  CheckCircleFill,
  XCircleFill,
  ExclamationCircle,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { formatCnpj, formatDateTimeExtratoList, normalizeText } from "../../../../utils";
import type { ExtractRow } from "../shared/extractListTypes";
import "./ExtractTable.css";

export interface ExtractTableProps {
  currentItems: ExtractRow[];
  handleSort: (key: string) => void;
  getSortIcon: (key: string) => string;
  onOpenHistory: (item: ExtractRow) => void;
  onGeneratePreview?: (item: ExtractRow) => void;
  mostrarColunaSolicitacao?: boolean;
  selectedYear?: number;
  formatDate?: (value: string | null | undefined) => string;
}

const getSituationConfig = (situacaoRaw: string | undefined) => {
  const s = normalizeText(situacaoRaw);

  if (s.includes("analise nao finalizada")) {
    return { icon: <PauseCircleFill color="#D7961E" />, className: "status-plain" };
  }

  if (s.includes("extrato pendente")) {
    return { icon: <Search />, className: "status-pendente-analista" };
  }

  if (s.includes("extrato enviado")) {
    return { icon: <SendFill />, className: "status-enviado" };
  }

  if (s.includes("extrato validado")) {
    return { icon: <CheckCircleFill color="#067647" />, className: "status-plain" };
  }

  if (s.includes("inapto")) {
    return { icon: <XCircleFill color="#BA0C2F" />, className: "status-plain" };
  }

  if (s.includes("recusado") || s.includes("nao validado") || s.includes("naovalidado")) {
    return { icon: <FileEarmarkXFill />, className: "status-adequacao-recusa" };
  }

  return { icon: <ExclamationCircle />, className: "status-default" };
};

const ExtractTable = ({
  currentItems,
  handleSort,
  getSortIcon,
  onOpenHistory,
  mostrarColunaSolicitacao = false,
  selectedYear,
  formatDate = formatDateTimeExtratoList,
}: ExtractTableProps) => {
  const navigate = useNavigate();

  const renderTooltip = (text: string) => (
    <Tooltip id={`tooltip-${String(text || "").replace(/\s+/g, "-")}`}>
      {text}
    </Tooltip>
  );

  const withTooltip = (
    text: string,
    children: React.ReactNode,
    placement: Placement = "top",
  ) => {
    const title = String(text ?? "").trim();
    if (!title) return children;

    return (
      <OverlayTrigger
        placement={placement}
        delay={{ show: 250, hide: 120 }}
        overlay={renderTooltip(title)}
      >
        <span className="tooltip-target">{children}</span>
      </OverlayTrigger>
    );
  };

  const handleEdit = async (item: ExtractRow) => {
    if (item.questionario && String(item.questionario).length > 0) {
      try {
        const idSindicato = item.idSindicato ?? item.id;
        if (!idSindicato) return;
        navigate(`/extract/${item.questionario}/${idSindicato}`, {
          state: {
            selectedYear: selectedYear ?? item.ano,
            solicitacao: item.solicitacao,
          },
        });
      } catch (error) {
        console.error("Erro ao navegar para o extrato:", error);
      }
    }
  };

  return (
    <Table bordered hover responsive className="extract-table">
      <thead>
        <tr>
          <th>{withTooltip("CNPJ", "CNPJ")}</th>

          <th onClick={() => handleSort("sigla")} className="sortable-header">
            {withTooltip("Sigla", <>Sigla {getSortIcon("sigla")}</>)}
          </th>

          {mostrarColunaSolicitacao ? (
            <th
              onClick={() => handleSort("solicitacao")}
              className="sortable-header"
            >
              {withTooltip(
                "Solicitação",
                <>Solicitação {getSortIcon("solicitacao")}</>,
              )}
            </th>
          ) : null}

          <th
            onClick={() => handleSort("situacao")}
            className="sortable-header extract-col-situacao"
          >
            {withTooltip("Situação", <>Situação {getSortIcon("situacao")}</>)}
          </th>

          <th onClick={() => handleSort("envio")} className="sortable-header">
            {withTooltip("Envio Extrato", <>Envio Extrato {getSortIcon("envio")}</>)}
          </th>

          <th
            onClick={() => handleSort("atualizacao")}
            className="sortable-header"
          >
            {withTooltip(
              "Última Atualização",
              <>Última Atualização {getSortIcon("atualizacao")}</>,
            )}
          </th>

          <th className="text-center">{withTooltip("Ações", "Ações")}</th>
        </tr>
      </thead>

      <tbody>
        {currentItems.map((item, index) => {
          const temIdentificador =
            !!item.questionario && Number(item.questionario) > 0;
          const anoLinha = selectedYear ?? item.ano ?? 2026;
          const legacyExtrato = Number(anoLinha) <= 2025;
          const podeGerarExtrato = legacyExtrato
            ? ["extrato pendente", "extrato enviado"].includes(
                normalizeText(item.situacao),
              )
            : item.situacaoId != null && item.situacaoId !== 1;
          const habilitado = temIdentificador && podeGerarExtrato;

          const isEditDisabled = !temIdentificador;

          const { icon, className } = getSituationConfig(item.situacao);

          return (
            <tr key={index}>
              <td>
                {withTooltip(formatCnpj(item.cnpj), formatCnpj(item.cnpj))}
              </td>

              <td>
                {withTooltip(
                  item.nome || item.sigla || "",
                  <span>{item.sigla}</span>,
                )}
              </td>

              {mostrarColunaSolicitacao ? (
                <td>
                  {withTooltip(
                    item.solicitacao || "-",
                    item.solicitacao || "-",
                  )}
                </td>
              ) : null}

              <td className="extract-col-situacao">
                {withTooltip(
                  item.situacao || "-",
                  <div className="situation-cell">
                    <span className={`situation-icon ${className}`}>
                      {icon}
                    </span>
                    <span className="situation-text">
                      {item.situacao || "-"}
                    </span>
                  </div>,
                )}
              </td>

              <td>
                {withTooltip(formatDate(item.envio), formatDate(item.envio))}
              </td>

              <td>
                {withTooltip(
                  formatDate(item.atualizacao),
                  formatDate(item.atualizacao),
                )}
              </td>

              <td>
                <div className="action-buttons d-flex gap-2 justify-content-center">
                  <Button
                    className="btn-edit"
                    size="sm"
                    onClick={() => handleEdit(item)}
                    disabled={!habilitado}
                    style={
                      !habilitado ? { opacity: 0.2, cursor: "not-allowed" } : {}
                    }
                    title={
                      isEditDisabled
                        ? "Ainda não iniciado"
                        : !podeGerarExtrato
                          ? "Análise não finalizada"
                          : "Gravar extrato"
                    }
                  >
                    <FaEdit />
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onOpenHistory(item)}
                    title="Ver histórico"
                  >
                    <FaClock />
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default ExtractTable;
